<template>
  <section
    class="filter-bar border rounded bg-body-emphasis p-3"
    aria-label="Conversation filters"
  >
    <div class="btn-group" aria-label="Search by">
      <button
        class="btn btn-sm"
        :class="
          model.mode === 'company' ? 'btn-primary' : 'btn-outline-primary'
        "
        :aria-pressed="model.mode === 'company'"
        @click="setMode('company')"
      >
        Company
      </button>
      <button
        class="btn btn-sm"
        :class="
          model.mode === 'account' ? 'btn-primary' : 'btn-outline-primary'
        "
        :aria-pressed="model.mode === 'account'"
        @click="setMode('account')"
      >
        Account
      </button>
    </div>
    <CompanySearchDropdown
      v-if="model.mode === 'company'"
      :modelValue="model.companyId"
      :withLabel="false"
      :withDescription="false"
      placeholder="All companies"
      @update:modelValue="
        update({ companyId: $event === null ? null : Number($event) })
      "
    />
    <AccountSearchDropdown
      v-else
      :modelValue="model.accountId"
      @update:modelValue="update({ accountId: $event })"
    />
    <MultiSelect
      :modelValue="model.types"
      :options="typeOptions"
      optionLabel="label"
      optionValue="value"
      placeholder="All types"
      :maxSelectedLabels="1"
      selectedItemsLabel="{0} types"
      aria-label="Conversation types"
      @update:modelValue="update({ types: $event })"
    />
    <select
      :value="period"
      class="form-select form-select-sm"
      aria-label="Period"
      @change="setPeriod(($event.target as HTMLSelectElement).value)"
    >
      <option value="all">All time</option>
      <option value="30">Last 30 days</option>
      <option value="90">Last 90 days</option>
      <option value="custom">Custom dates</option>
    </select>
    <div class="d-flex flex-column gap-1 small">
      <label class="d-flex align-items-center gap-2"
        ><input
          type="checkbox"
          class="form-check-input mt-0"
          :checked="!model.includeStaff"
          @change="
            update({
              includeStaff: !($event.target as HTMLInputElement).checked,
            })
          "
        />Hide Soldera staff</label
      >
      <label class="d-flex align-items-center gap-2"
        ><input
          type="checkbox"
          class="form-check-input mt-0"
          :checked="model.includeTest"
          @change="
            update({ includeTest: ($event.target as HTMLInputElement).checked })
          "
        />Include test companies</label
      >
    </div>
    <form @submit.prevent="submitJump">
      <input
        v-model="jumpId"
        class="form-control form-control-sm"
        type="number"
        min="1"
        step="1"
        aria-label="Jump to conversation id"
        placeholder="# Jump to conversation id"
      />
    </form>
    <div v-if="period === 'custom'" class="date-range d-flex flex-wrap gap-3">
      <label class="small"
        >Since (UTC)<input
          class="form-control form-control-sm"
          type="datetime-local"
          :value="model.since?.slice(0, 16)"
          @change="
            updateDate('since', ($event.target as HTMLInputElement).value)
          "
      /></label>
      <label class="small"
        >Until (UTC)<input
          class="form-control form-control-sm"
          type="datetime-local"
          :value="model.until?.slice(0, 16)"
          @change="
            updateDate('until', ($event.target as HTMLInputElement).value)
          "
      /></label>
    </div>
  </section>
</template>
<script setup lang="ts">
import MultiSelect from "primevue/multiselect";
import { computed, ref, watch } from "vue";
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import AccountSearchDropdown from "@/components/common/AccountSearchDropdown.vue";
import type { AiConversationFilters } from "./aiConversationsTypes";
import {
  conversationTypeLabel,
  conversationTypeLabels,
} from "./aiConversationsPresenters";

const props = defineProps<{ conversationId: number | null }>();
const model = defineModel<AiConversationFilters>({ required: true });
const emit = defineEmits<{ jump: [id: number] }>();
const jumpId = ref("");
const customDates = ref(false);
let pendingDates: Pick<AiConversationFilters, "since" | "until"> | null = null;
const typeOptions = computed(() =>
  [
    ...new Set([...Object.keys(conversationTypeLabels), ...model.value.types]),
  ].map((value) => ({ value, label: conversationTypeLabel(value) }))
);
const period = computed(() => {
  if (customDates.value || model.value.until) return "custom";
  if (!model.value.since) return "all";
  const days = (Date.now() - Date.parse(model.value.since)) / 86400000;
  return (
    [30, 90].find((value) => Math.abs(value - days) < 0.5)?.toString() ??
    "custom"
  );
});
watch(
  () => props.conversationId,
  (id) => {
    jumpId.value = id?.toString() ?? "";
  },
  { immediate: true }
);
watch(model, (value) => {
  // Preserve custom editing through local filter updates, but reset it for restored filters.
  if (
    !pendingDates ||
    value.since !== pendingDates.since ||
    value.until !== pendingDates.until
  ) {
    customDates.value = false;
  }
  pendingDates = null;
});

function update(patch: Partial<AiConversationFilters>) {
  const value = { ...model.value, ...patch };
  pendingDates = { since: value.since, until: value.until };
  model.value = value;
}
function setMode(mode: AiConversationFilters["mode"]) {
  update({ mode, companyId: null, accountId: null });
}
function setPeriod(value: string) {
  customDates.value = value === "custom";
  if (value === "custom") return;
  update({
    since:
      value === "all"
        ? undefined
        : new Date(Date.now() - Number(value) * 86400000).toISOString(),
    until: undefined,
  });
}
function updateDate(key: "since" | "until", value: string) {
  update({ [key]: value ? new Date(`${value}Z`).toISOString() : undefined });
}
function submitJump() {
  const id = Number(jumpId.value);
  if (Number.isSafeInteger(id) && id > 0) emit("jump", id);
}
</script>
<style scoped lang="scss">
.filter-bar {
  display: grid;
  grid-template-columns:
    auto minmax(200px, 1fr) minmax(140px, 180px)
    140px auto 185px;
  gap: 0.75rem;
  align-items: center;
}
.filter-bar > * {
  min-width: 0;
}
.date-range {
  grid-column: 1 / -1;
}
@media (max-width: 1250px) {
  .filter-bar {
    grid-template-columns: auto minmax(180px, 1fr) minmax(140px, 1fr);
  }
}
@media (max-width: 600px) {
  .filter-bar {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
